<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, getCurrentInstance, onMounted, onUnmounted, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import _ from "lodash"
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import Spinner from "@/components/ui/Spinner.vue"
import StreamService from '@/services/StreamService'
import SubSecondStats from '@/components/SubSecondStream/SubSecondStats.vue'
import StreamSidebar from '../../components/ui/StreamSidebar.vue'
import AlertModal from '@/components/modals/AlertModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import Title from '../../components/ui/Title.vue'
import PaywallService from '@/services/PaywallService'
import ToggleStreamModal from '@/components/modals/ToggleStreamModal.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import TabGroup from '../../components/Atoms/TabGroup.vue'
import TabItem from '../../components/Atoms/TabItem.vue'
import IconBase from '../../components/icon/IconBase.vue'
import BackButton from '@/components/Atoms/BackButton.vue'
import IconPlay from '../../components/icon/IconPlay.vue'
import IconBin from '../../components/icon/IconBin.vue'
import IconBookOpen from '../../components/icon/IconBookOpen.vue'
import DropDownMenu from '../../components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../../components/Atoms/DropDownMenuItem.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import HeaderHelperLinks from '@/components/Moleculas/HeaderHelperLinks.vue'
import IconElipsis from '@/components/icon/IconElipsis.vue';
import { track, EVENTS } from '@/lib/tracking'

const { notify } = useNotification()
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const route = useRoute()
const router = useRouter()

const sssDropdown = ref(null)
const notAllowed = ref(false)
const isVideoListLoading = ref(false)
const scopeAlive = ref(true)
const statusProcessing = ref(false)
const trialSubscription = ref(null)
const processing = ref(false)
const processingMessage = ref(null)
const detailResolving = ref(true)
const detailSyncing = ref(false)
const detailRequestToken = ref(0)
const trackedPageStreamId = ref(null)
const stream = ref(null)
const streamName = ref(null)
const backupStreamAlive = ref(false)
const videoFiles = ref([])
const scheduledAlert = ref(true)
const isRtmpPullEnabled = ref(null)
const streamDvrEnabled = ref(null)
const childViewList = ref([
	{
		streamType: 'subsecond',
		name: 'Destinations',
		viewName: 'SubSecondManageDashboard'
	},
	{
		streamType: 'subsecond',
		name: 'Analytics',
		viewName: 'SubSecondManageAnalyticsLiveInputHealthCharts'
	},
	{
		streamType: 'subsecond',
		name: 'Settings',
		viewName: 'SubSecondSecuritySettings'
	},
])
const doc = ref({
	subsecond: {
		text: 'How Sub-Second Streaming works',
		link: 'https://docs.castr.com/en/articles/8693777-sub-second-streaming',
		iframe: 'https://www.youtube.com/embed/Txy2k4IAIWI'
	},
})

const emit = defineEmits({
	'updateTitle': () => true,
})


// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

const toggleProcessing = ref(false)

const userEmail = computed(() => store.state.User.email)

const baseSubscription = computed(() => store.getters["User/baseSubscription"])

const activeTeam = computed(() => store.state.User.activatedTeam)

const memberPermission = computed(() => activeTeam.value && store.state.User?.memberPermission)

const streamAlive = computed(() => stream.value?.enabled && mediaPulse.value?.alive)
const streamReady = computed(
	() => String(stream.value?._id || '') === String(route.params.streamId || '')
)

// Analytics renders full-width (sidebar hidden) so charts/session history have room
const isAnalyticsView = computed(() => (route.name || '').includes('Analytics') || route.path.includes('/analytics'))

const applyStream = (details, metadata) => {
	const next = _.cloneDeep(details || {})
	if (metadata) next.metadata = metadata
	// HLS stays bound to the original region, so the UI region is a separate field.
	if (!next.uiRegion) next.uiRegion = next.region
	stream.value = next
	streamDvrEnabled.value = next.metadata?.dvrArchive ?? null
	if (localStorage.getItem('notAllowed' + next.type)) {
		notAllowed.value = localStorage.getItem('notAllowed' + next.type) == 'true'
	}
	subscribeMediaPulse(stream.value)
	if (trackedPageStreamId.value !== next._id) {
		trackedPageStreamId.value = next._id
		window.trackEvent('Page Viewed', {
			page: 'Stream',
			streamId: next._id,
			streamType: next.type,
		})
		emit('updateTitle', _.toUpper(next.name || '') + ' - Castr Restreams')
	}
	if (baseSubscription.value) {
		trialSubscription.value = /trial/gi.test(baseSubscription.value.package.name)
	}
}

const loadStream = async (id) => {
	const token = ++detailRequestToken.value
	const isCurrentRequest = () => (
		scopeAlive.value
		&& token === detailRequestToken.value
		&& String(route.params.streamId) === String(id)
	)
	let seedSnapshot = null

	detailResolving.value = true
	detailSyncing.value = true

	const seedFromList = () => {
		if (!isCurrentRequest() || streamReady.value) return false
		const listed = store.getters['Streams/getStreamById'](id)
		if (!listed) return false
		applyStream(listed)
		seedSnapshot = JSON.stringify(stream.value)
		detailResolving.value = false
		return true
	}

	seedFromList()

	void store.dispatch('User/getactiveTeam', null, { root: true }).catch((err) => {
		if (isCurrentRequest()) console.error('Failed to load team', err)
	})
	void store.dispatch('Streams/getStreams').then(() => seedFromList()).catch((err) => {
		if (isCurrentRequest()) console.error('Failed to synchronize streams', err)
	})

	try {
		const [streamDetails, metadata] = await Promise.all([
			StreamService.getStream(id),
			StreamService.getStreamMetadata(id),
		])
		if (!isCurrentRequest()) return

		const seededStateUnchanged = seedSnapshot !== null
			&& JSON.stringify(stream.value) === seedSnapshot
		if (seedSnapshot === null || seededStateUnchanged) {
			applyStream(streamDetails, metadata)
		}
		detailResolving.value = false
	} catch (err) {
		if (!isCurrentRequest()) return
		notify({
			group: 'error',
			title: err.error || 'Could not load this Sub-Second stream',
			text: err.message,
		})
		if (!streamReady.value) {
			await router.push({ name: 'SubSecondDashboard' })
			return
		}
		detailResolving.value = false
	} finally {
		if (isCurrentRequest()) detailSyncing.value = false
	}
}

onMounted(() => {
	void loadStream(route.params.streamId)
})

watch(
	() => route.params.streamId,
	(id, previousId) => {
		if (previousId !== undefined && String(id) !== String(previousId)) {
			void loadStream(id)
		}
	}
)

onUnmounted(() => {
	scopeAlive.value = false
	detailRequestToken.value += 1
	unsubscribeMediaPulse()
})

const checkDelPermission = () => {
	let permission = memberPermission.value && !memberPermission.value.liveStreamDelete
	return permission
}

const checkTogglePermission = () => {
	let permission = memberPermission.value && !memberPermission.value.liveStreamToggle
	return permission
}

const openCreatestream = () => {
	showModal('modal-add-stream')
}

const onStreamUpdates = (updates) => {
	stream.value = _.assign({}, stream.value, updates)
}

const setupStream = async () => {
	const id = route.params.streamId
	try {
		const [streamDetails, metadata] = await Promise.all([
			StreamService.getStream(id),
			StreamService.getStreamMetadata(id),
		])
		if (!scopeAlive.value || String(route.params.streamId) !== String(id)) return
		applyStream(streamDetails, metadata)
	} catch (err) {
		notify({
			group: 'error',
			title: err.error,
			text: err.message
		})
		if (!streamReady.value) {
			router.push({ name: 'SubSecondDashboard' })
		}
	}
}

const onStreamNameChange = async (newName) => {
	if (!newName) return

	if (stream.value.name === newName) return

	// try changing stream name
	try {
		await StreamService.setStreamName(stream.value._id, newName)
		PaywallService.updateStreamPaywall(stream.value._id, { title: newName }).catch(err => {
			console.error('update paywall stream name failed: ', err)
		})

		// track event
		window.trackEvent('Stream Renamed', {
			streamId: stream.value._id,
			oldName: stream.value.name,
			newName: streamName.value,
		})
	} catch (err) {
		notify({
			group: 'error',
			title: "Couldn't change stream name",
			text: err.message
		})
	}
}

const toggleStatus = async (ev, newStatus) => {
	ev && ev.preventDefault()
	ev && ev.stopPropagation()

	const oldStatus = stream.value.enabled
	newStatus = newStatus === undefined ? !oldStatus : newStatus

	// this.statusProcessing = true;
	stream.value.enabled = newStatus
	if (!newStatus) {
		scheduledAlert.value = true
	}
	try {
		await StreamService.toggleStream(stream.value._id)
		// this.stream.enabled = newStatus;

		track(newStatus ? EVENTS.STREAM_ENABLED : EVENTS.STREAM_DISABLED, {
			streamType: 'subsecond'
		})
	} catch (err) {
		stream.value.enabled = oldStatus

		notify({
			group: 'error',
			title: "Couldn't toggle stream status",
			text: err.message
		})

		if (err.message && err.message.indexOf('upgrade') > -1) {
			showModal('billing-prompt')
		}
	}

	statusProcessing.value = false
}

const onToggleProcessing = (status) => {
	toggleProcessing.value = status
}

const onToggleDone = () => {
	setupStream()
}

const requestStreamDelete = () => {
	if (activeTeam.value && checkDelPermission()) return
	if (!activeTeam || !(((stream.value.type === 'live') && memberPermission.value && !memberPermission.value.liveStreamDelete))) {
		showModal('modal-confirm')
	}
	sssDropdown.value.toggleDropdown()
}

const onStreamDeleteConfirm = async () => {
	processing.value = true
	processingMessage.value = 'Deleting stream'
	// try deleting stream
	try {
		await StreamService.deleteStream(stream.value._id)
		// Force refresh to remove deleted stream from store
		await store.dispatch('Streams/getStreams', true)
		
		// track event
		window.trackEvent('Stream Deleted', {
			streamId: stream.value._id,
			streamName: stream.value.name,
			streamType: stream.value.type,
		})
		router.push({ name: 'SubSecondDashboard' })


	} catch (err) {
		// redirect to stream list
		notify({
			group: 'error',
			title: err.error,
			text: err.message
		})
	}

	processing.value = false
}

const showStreamToggleModal = (ev) => {
	ev && ev.preventDefault()
	ev && ev.stopPropagation()
	if (notAllowed.value + stream.value.type && !stream.value.enabled) {
		showModal('increase-limit')
	}
	if (activeTeam.value && checkTogglePermission()) return
	if (!notAllowed.value || (notAllowed.value && stream.value.enabled)) {
		showModal('modalToggleStream' + stream.value._id, '#btnShow')
	}
	sssDropdown.value.toggleDropdown()
}

const isTabActive = (viewItem) => {
	if (route.name === viewItem.viewName) return true
	// Keep the section tab highlighted across its sub-routes (e.g. Analytics → Session History)
	if (viewItem.name === 'Analytics') return route.path.includes('/analytics')
	if (viewItem.name === 'Settings') return route.path.includes('/settings')
	return false
}

const changeRouteQuery = (viewItem) => {
	if (route.name != viewItem.viewName) {
		router.push({ name: viewItem.viewName })
	} else return
}
</script>

<template>
	<div class="w-full container detail-shell px-4 mt-4 lg:pt-8 lg:self-start"
		:data-detail-syncing="detailSyncing || undefined">
		<div v-if="processing || detailResolving || !streamReady" class="flex items-center justify-center w-full min-h-[70vh]" aria-busy="true">
			<Spinner classes="text-surface-8" spinner-color="var(--c-surface-8)" spinner-size="22px" />
		</div>
		<div class="mt-6" :class="isAnalyticsView ? '' : 'xl:grid xl:grid-cols-[minmax(0,1fr)_420px] gap-4 grid-rows-[auto_1fr]'" v-else>
			<div class="">
				<div class="flex items-center flex-wrap w-full gap-x-4">
					<BackButton to="/sub-second" />
					<Title :editable="true" :title="stream?.name" @changed="onStreamNameChange"
						titleClasses="-mr-2 px-[0.5rem] rounded-md hover:bg-surface-3 line-clamp-2 text-lg font-semibold min-h-8 min-w-0"
						edit-class="bg-surface-3 line-clamp-none" />
					<HeaderHelperLinks class="ml-auto" :howItWorksLink="doc[stream.type].link" :howItWorksText="doc[stream.type].text" tutorialLink="48ofjE3WZ064-SuIElpIr" />
					<drop-down-menu v-if="activeTeam && checkDelPermission()" ref="sssDropdown" size="sm" isIcon no-border
						type="secondary" class="inline-flex" position="right">
						<template #toggle-button>
							<icon-base class="ml-auto">
								<icon-elipsis />
							</icon-base>
						</template>
						<template #menu-items>
							<drop-down-menu-item class="w-full" @click="showStreamToggleModal">
								{{ stream.enabled ? 'Disable' : 'Enable' }}
							</drop-down-menu-item>
							<drop-down-menu-item class="w-full" @click="requestStreamDelete">
								Delete
							</drop-down-menu-item>
						</template>
					</drop-down-menu>
				</div>
			</div>
			<aside v-if="!isAnalyticsView" class="row-span-2 mt-4 xl:mt-0">
				<!-- no primary/backup failover pulse for sub-second; sidebar uses its prop default -->
				<stream-sidebar v-if="streamType !== 'scheduled'" :stream="stream" ref="sidebarStreamPreviewPane"
					:stream-alive="streamAlive" :backup-stream-alive="backupStreamAlive"
					:media-pulse="mediaPulse" :isRtmpPullEnabled="isRtmpPullEnabled"
					@stream-updated="onStreamUpdates" @show-stream-toggle-modal="showStreamToggleModal($event)" class="mb-4" />
				<div class="">
					<div v-if="statusProcessing">
						<i class="fas fa-spinner fa-spin" />
					</div>
					<div v-else>
						<div class="!hidden md:!flex justify-end">
							<Tooltip :active="memberPermission">
								<FormButton v-if="stream.enabled" :disabled="toggleProcessing || memberPermission" type="secondary"
									class="mr-2" @click="showStreamToggleModal">
									{{ stream.enabled ? 'Disable' : 'Enable' }}
								</FormButton>
								<template #content>
									<span class="text-surface-8 text-small">
										You don’t have permission. Contact the team owner at <a class="text-surface-8 text-small"
											:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</span>
								</template>
							</Tooltip>
							<Tooltip :active="memberPermission">
								<FormButton :disabled="memberPermission" type="danger" @click="requestStreamDelete">
									<icon-base>
										<icon-bin />
									</icon-base>
								</FormButton>
								<template #content>
									<span class="text-surface-8 text-small">
										You don’t have permission. Contact the team owner at <a class="text-surface-8 text-small"
											:href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</span>
								</template>
							</Tooltip>
						</div>
					</div>
				</div>
			</aside>
			<main class="">
				<sub-second-stats :stream="stream" :media-pulse="mediaPulse" :subscription="baseSubscription"
					:video-files="videoFiles" />
				<div class="bg-surface-2 rounded-xl shadow-[0_2px_6px_rgba(0,0,0,0.28)] p-4 md:p-5 mt-4">
					<TabGroup type="segmented" custom-classes="mb-4 overflow-x-auto no-scrollbar">
						<TabItem v-for="(viewItem, index) in childViewList" :key="index"
							:is-active="isTabActive(viewItem)"
							@click="changeRouteQuery(viewItem)">
							{{ viewItem.name }}
						</TabItem>
					</TabGroup>
					<div class="tab-content relative mt-4">
						<router-view v-slot="{ Component }">
							<component :is="Component" :stream="stream" :stream-alive="streamAlive" :media-pulse="mediaPulse"
								:trial-subscription="trialSubscription"
								@stream-updated="onStreamUpdates" @refresh-stream="setupStream" />
						</router-view>
						<Spinner v-if="isVideoListLoading" text="Retrieving data..." classes="text-surface-8 my-3"
							spinner-color="var(--c-surface-8)" spinner-size="15px" />
					</div>
				</div>
			</main>
		</div>
		<confirm-modal message="Would you like to delete this stream and all of its content?" modal-type="danger"
			@modal-confirm="onStreamDeleteConfirm" />

		<confirm-modal
			message="Attention: Multistream has been discontinued. Set up a new Live Stream today for uninterrupted streaming with enhanced features, at no extra cost."
			modal-id="create-aio" ok-text="Create Live Stream" cancel-text="Cancel" @modal-confirm="openCreatestream" />
		<confirm-modal modalId="delete-vod-stream" message="Permanently delete all files in this folder?"
			modal-type="danger" body="Yours files in VOD section inside the stream will also be deleted."
			@modal-confirm="onStreamDeleteConfirm" />
		<confirm-modal modalId="delete-vod-stream-showcase" message="Permanently delete all files in this folder?"
			modal-type="danger"
			body="This folder contains video(s) which are selected in the showcase. Deleting the folder will remove videos from the showcase as well."
			@modal-confirm="onStreamDeleteConfirm" />

		<confirm-modal modal-size="sm" modal-id="scheduled-status-warning" modal-type="danger"
			message="Stream is already scheduled" ok-text="Disable anyway" cancel-text="Cancel"
			body="If you disable the stream, it won’t start at the scheduled time."
			@modal-confirm="(scheduledAlert = false); toggleStatus()" />

		<alert-modal modal-id="alert-chat-down"
			message="We have encountered unusual behavior in our chat app and would appreciate your patience while our team gets things back online."
			ok-text="I Understand" class="text-center" />
		<toggle-stream-modal v-if="stream" :stream="stream" @modal-toggle="toggleStatus"
			@toggle-processing="onToggleProcessing" @toggle-done="onToggleDone" />
	</div>
</template>

<style scoped></style>
